import { useMemo } from 'react';
import { Chip } from '@heroui/chip';
import { IStudentAboutStaffResponse } from '@/entities/Staff';
import { AcademicRecordType, ACADEMIC_RECORD_TYPE_LABELS } from '@/shared/types/common';
import { Section } from '@/shared/ui/Section';
import { normalize } from '@/shared/lib/utils/normalize';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import styles from './StaffProfile.module.scss';

interface StaffProfileProps {
  data: IStudentAboutStaffResponse;
}

export const StaffProfile = ({ data }: StaffProfileProps) => {
  const fullName = useMemo(
    () => `${data.firstName} ${data.lastName}`.trim() || data.username,
    [data.firstName, data.lastName, data.username],
  );

  const { rolesPerCourse, uniqueCategories } = useMemo(() => {
    const rolesPerCourse: { courseId: number; label: string }[] = [];
    const uniqueCategories: string[] = [];
    const categorySeen = new Set<string>();

    data.courses.forEach((course) => {
      if (Object.hasOwn(ACADEMIC_RECORD_TYPE_LABELS, course.academicRole)) {
        const roleLabel = ACADEMIC_RECORD_TYPE_LABELS[course.academicRole as AcademicRecordType];
        rolesPerCourse.push({
          courseId: course.id,
          label: `${roleLabel} ${course.title}`,
        });
      }

      const category = course.category;
      if (category && !categorySeen.has(category)) {
        categorySeen.add(category);
        uniqueCategories.push(category);
      }
    });

    return { rolesPerCourse, uniqueCategories };
  }, [data.courses]);

  return (
    <Section className={styles.root}>
      <div className="flex flex-col gap-y-2">
        <UserAvatar avatar={data.avatar} userName={fullName} href={`/staff/${data.id}`} />
        {rolesPerCourse.length > 0 && (
          <div className="flex flex-col gap-y-2">
            {rolesPerCourse.map(({ courseId, label }) => (
              <p key={courseId} className="text-grey-secondary text-sm">
                {label}
              </p>
            ))}
          </div>
        )}
        {data.department ? (
          <p className="text-grey-secondary text-sm">{data.department}</p>
        ) : null}
      </div>
      {uniqueCategories.length > 0 ? (
        <ul className="flex gap-x-2">
          {uniqueCategories.map((category) => (
            <li key={category}>
              <Chip className="bg-red text-white">{normalize(category, { isUpperCase: true })}</Chip>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
};
